import React, { useEffect, useRef } from 'react';
import { VERTEX_SHADER } from '../constants';
import { ShaderError } from '../types';

interface VisualizerProps {
  code: string;
  onError?: (error: ShaderError | null) => void;
}

const Visualizer: React.FC<VisualizerProps> = ({ code, onError }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const glRef = useRef<WebGL2RenderingContext | null>(null);
  const programRef = useRef<WebGLProgram | null>(null);
  const requestRef = useRef<number | undefined>(undefined);
  const startTimeRef = useRef<number>(Date.now());
  const mouseRef = useRef<[number, number]>([0, 0]);
  const quadBufferRef = useRef<WebGLBuffer | null>(null);

  const initGL = () => {
    const canvas = canvasRef.current;
    if (!canvas) return null;

    const gl = canvas.getContext('webgl2', { antialias: true, alpha: false });
    if (!gl) {
      console.error("WebGL 2 not supported");
      return null;
    }
    glRef.current = gl;

    // Create persistent quad buffer
    const buffer = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([
      -1.0, -1.0, 1.0, -1.0, -1.0, 1.0,
      -1.0, 1.0, 1.0, -1.0, 1.0, 1.0
    ]), gl.STATIC_DRAW);
    quadBufferRef.current = buffer;

    const resize = () => {
      canvas.width = window.innerWidth * window.devicePixelRatio;
      canvas.height = window.innerHeight * window.devicePixelRatio;
      gl.viewport(0, 0, canvas.width, canvas.height);
    };

    window.addEventListener('resize', resize);
    resize();

    return () => window.removeEventListener('resize', resize);
  };

  const compileShader = (gl: WebGL2RenderingContext, source: string, type: number) => {
    const shader = gl.createShader(type);
    if (!shader) return null;
    gl.shaderSource(shader, source.trim());
    gl.compileShader(shader);

    if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
      const error = gl.getShaderInfoLog(shader);
      gl.deleteShader(shader);
      throw new Error(error || 'Shader compilation failed');
    }
    return shader;
  };

  const updateProgram = () => {
    const gl = glRef.current;
    if (!gl) return;

    try {
      const vs = compileShader(gl, VERTEX_SHADER, gl.VERTEX_SHADER);
      const fs = compileShader(gl, code, gl.FRAGMENT_SHADER);

      if (!vs || !fs) return;

      const program = gl.createProgram();
      if (!program) return;
      gl.attachShader(program, vs);
      gl.attachShader(program, fs);
      gl.linkProgram(program);

      if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
        throw new Error(gl.getProgramInfoLog(program) || 'Program linking failed');
      }

      programRef.current = program;
      onError?.(null);
    } catch (e: any) {
      console.warn("Shader Error:", e.message);
      const match = e.message.match(/ERROR: 0:(\d+):/);
      onError?.({
        line: match ? parseInt(match[1]) : 0,
        message: e.message
      });
    }
  };

  const render = () => {
    const gl = glRef.current;
    const program = programRef.current;
    const quadBuffer = quadBufferRef.current;

    if (!gl || !program || !quadBuffer) {
      requestRef.current = requestAnimationFrame(render);
      return;
    }

    const canvas = canvasRef.current!;
    gl.useProgram(program);

    // Bind quad buffer
    gl.bindBuffer(gl.ARRAY_BUFFER, quadBuffer);
    const positionLoc = gl.getAttribLocation(program, 'position');
    if (positionLoc !== -1) {
      gl.enableVertexAttribArray(positionLoc);
      gl.vertexAttribPointer(positionLoc, 2, gl.FLOAT, false, 0, 0);
    }

    // Set uniforms
    const timeLoc = gl.getUniformLocation(program, 'u_time');
    const resLoc = gl.getUniformLocation(program, 'u_resolution');
    const mouseLoc = gl.getUniformLocation(program, 'u_mouse');

    gl.uniform1f(timeLoc, (Date.now() - startTimeRef.current) / 1000);
    gl.uniform2f(resLoc, canvas.width, canvas.height);
    gl.uniform2f(mouseLoc, mouseRef.current[0], mouseRef.current[1]);

    gl.drawArrays(gl.TRIANGLES, 0, 6);
    requestRef.current = requestAnimationFrame(render);
  };

  useEffect(() => {
    const cleanupResize = initGL();
    requestRef.current = requestAnimationFrame(render);
    return () => {
      if (requestRef.current) cancelAnimationFrame(requestRef.current);
      if (cleanupResize) cleanupResize();
    };
  }, []);

  useEffect(() => {
    updateProgram();
  }, [code]);

  const handleMouseMove = (e: React.MouseEvent) => {
    const rect = canvasRef.current?.getBoundingClientRect();
    if (rect) {
      const dpr = window.devicePixelRatio;
      mouseRef.current = [
        (e.clientX - rect.left) * dpr,
        (rect.height - (e.clientY - rect.top)) * dpr
      ];
    }
  };

  return (
    <canvas
      ref={canvasRef}
      onMouseMove={handleMouseMove}
      className="fixed inset-0 w-full h-full"
      style={{ touchAction: 'none' }}
    />
  );
};

export default Visualizer;